<template>
  <MainLayout>
    <div class="gift-card-view">
      <div class="top-title">
        <h1>Gift Card</h1>
      </div>

      <div class="gift-card-content" v-if="ticket && ticket.product">
        <h2>Seu brinde foi resgatado!</h2>
        <p class="gift-card-info" v-if="ticket.product_item">
          Use o número e a senha abaixo para utilizar seu gift card.
          Guarde essas informações e não compartilhe com outras pessoas.
        </p>
        <div class="gift-card-flip" :class="{ 'no-flip': !canFlip }" @click="toggleFlip">
          <div class="gift-card-inner" :class="{ flipped }">
            <!-- Frente -->
            <div class="gift-card face-front" :aria-hidden="flipped">
              <div class="gift-card-header">
                <!-- <div class="gift-card-image" v-if="image">
                  <img :src="image" alt="" />
                </div> -->
                <div class="gift-card-title">
                  <span class="gift-card-label">Gift Card</span>
                  <h2>{{ ticket.product.name }}</h2>
                </div>
              </div>

              <div class="gift-card-price" v-if="ticket.product.price">
                {{ ticket.product.price | toCurrency }}
              </div>

              <div class="gift-card-data" v-if="ticket.product_item && ticket.product_item.number">
                <div class="data-row">
                  <div class="data-text">
                    <span class="data-label">Número</span>
                    <span class="data-value">{{ ticket.product_item.number }}</span>
                  </div>
                  <button type="button" class="copy-btn" :tabindex="flipped ? -1 : 0"
                    @click.stop="copy(ticket.product_item.number, 'Número')">
                    <i class="fa-regular fa-copy"></i>
                  </button>
                </div>
              </div>

              <span class="flip-hint" v-if="canFlip">
                <i class="fa-solid fa-rotate"></i> Toque no cartão para ver a senha
              </span>
            </div>

            <!-- Verso -->
            <div class="gift-card face-back" :aria-hidden="!flipped">
              <!-- <div class="magnetic-strip"></div> -->

              <div class="barcode" v-if="barcode">
                <svg :viewBox="`0 0 ${barcode.width} 60`" preserveAspectRatio="none" role="img"
                  :aria-label="`Código de barras ${barcode.digits}`">
                  <rect x="0" y="0" :width="barcode.width" height="60" fill="#fff" />
                  <rect v-for="(bar, i) in barcode.bars" :key="i" :x="bar.x" y="4" :width="bar.w" height="52"
                    fill="#000" />
                </svg>
                <span class="barcode-digits">{{ barcode.digits }}</span>
              </div>

              <div class="gift-card-data" v-if="ticket.product_item && ticket.product_item.password">
                <div class="data-row">
                  <div class="data-text">
                    <span class="data-label">Senha</span>
                    <span class="data-value">{{ ticket.product_item.password }}</span>
                  </div>
                  <button type="button" class="copy-btn" :tabindex="flipped ? 0 : -1"
                    @click.stop="copy(ticket.product_item.password, 'Senha')">
                    <i class="fa-regular fa-copy"></i>
                  </button>
                </div>
              </div>

              <span class="flip-hint"><i class="fa-solid fa-rotate"></i> Toque no cartão para voltar</span>
            </div>
          </div>
        </div>
        <div class="gift-card-warning" v-if="!ticket.product_item">
          <p class="gift-card-info">
            Não foi possível exibir os dados do seu gift card aqui.
            Procure um atendente da loja e informe o número do ticket abaixo.
          </p>
          <p class="ticket-number">Ticket #{{ ticket.number }}</p>
        </div>
      </div>

      <p class="gift-card-message" v-else>{{ message }}</p>

      <center class="gift-card-actions">
        <button class="primary" @click="historyBack">Voltar</button>
      </center>
    </div>
  </MainLayout>
</template>

<script>
import MainLayout from "../layout/main";
import CampaignMixin from "../mixins/campaign.mixin.js";
import userMixin from "../mixins/user.mixin";

import { toEan13, ean13Modules } from "../js/ean13";
import "../filters/toCurrency";

export default {
  mixins: [CampaignMixin, userMixin],
  components: {
    MainLayout,
  },
  data() {
    return {
      ticket: null,
      message: "Carregando",
      flipped: false,
    };
  },
  computed: {
    // EAN-13 do número do gift card; null se o número não for um EAN-13 válido
    barcode() {
      const item = this.ticket && this.ticket.product_item;
      const digits = item ? toEan13(item.number) : null;
      if (!digits) return null;

      const QUIET = 9; // zona de silêncio, em módulos
      const modules = ean13Modules(digits);
      const bars = [];
      for (let i = 0; i < modules.length; i++) {
        if (modules[i] !== "1") continue;
        const last = bars[bars.length - 1];
        if (last && last.x + last.w === QUIET + i) last.w++;
        else bars.push({ x: QUIET + i, w: 1 });
      }
      return { digits, bars, width: modules.length + QUIET * 2 };
    },
    canFlip() {
      return !!(this.ticket && this.ticket.product_item && this.ticket.product_item.password);
    },
    image() {
      const images = this.ticket && this.ticket.product && this.ticket.product.images;
      return images && images[0] ? this.getImageThumbUrl(images[0]) : "";
    },
  },
  methods: {
    toggleFlip() {
      if (this.canFlip) this.flipped = !this.flipped;
    },
    copy(value, label) {
      const text = String(value);
      const done = () => this.$toasted.show(`${label} copiado`, { type: "success" });
      const fallback = () => {
        const el = document.createElement("textarea");
        el.value = text;
        el.style.position = "fixed";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.select();
        try {
          document.execCommand("copy");
          done();
        } catch (e) {
          this.$toasted.show("Não foi possível copiar", { type: "error" });
        }
        document.body.removeChild(el);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done, fallback);
      } else {
        fallback();
      }
    },
  },
  mounted() {
    this.getTicket(this.$route.params.id).then((response) => {
      if (response && response.ticket) this.ticket = response.ticket;
      else this.message = "Gift card não encontrado";
    });
  },
};
</script>

<style scoped lang="scss">
.gift-card-view {
  padding-bottom: 150px;
}

@media (max-width: 480px) {
  .gift-card-content {
    padding: 16px 12px;
  }

  .gift-card {
    padding: 16px;
    gap: 14px;
  }

  .gift-card-price {
    font-size: 24px;
  }

  .magnetic-strip {
    margin: 0 -16px;
  }
}

.gift-card-content {
  padding: 20px;
  max-width: 480px;
  margin: 0 auto;

  h2{
    text-align: center;
    margin-bottom: 20px;
  }
}

.gift-card-info {
  text-align: center;
  font-size: 14px;
  line-height: 1.4;
  margin-bottom: 30px;
}

.gift-card-warning {
  margin-top: 30px;

  .gift-card-info {
    margin-bottom: 12px;
    font-weight: bold;
  }

  .ticket-number {
    text-align: center;
    font-size: 20px;
    font-weight: bold;
    color: var(--primary);
  }
}

.gift-card-flip.no-flip {
  cursor: default;
}

.gift-card-message {
  text-align: center;
  padding: 20px;
}

.gift-card-actions {
  margin-top: 10px;
}

.gift-card-flip {
  perspective: 1200px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.gift-card-inner {
  display: grid;
  transform-style: preserve-3d;
  transition: transform 0.7s cubic-bezier(0.4, 0.2, 0.2, 1);

  &.flipped {
    transform: rotateY(180deg);
  }

  // frente e verso ocupam a mesma célula: a altura é a do maior
  > .gift-card {
    grid-area: 1 / 1;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
  }

  > .face-back {
    transform: rotateY(180deg);
    justify-content: center;
  }

  // a face escondida ainda recebe o clique em alguns navegadores e
  // ficava por cima dos botões da face visível
  > .face-front,
  > .face-back {
    pointer-events: none;
  }

  > .face-front {
    pointer-events: auto;
  }

  &.flipped {
    > .face-front {
      pointer-events: none;
    }

    > .face-back {
      pointer-events: auto;
    }
  }
}

.magnetic-strip {
  height: 40px;
  margin: 0 -20px;
  background: rgba(0, 0, 0, 0.55);
}

.barcode {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px 8px 6px;
  border-radius: 8px;
  background: #fff;
  color: #000;

  svg {
    display: block;
    width: 100%;
    height: 64px;
  }

  .barcode-digits {
    font-family: "Courier New", monospace;
    font-size: 13px;
    letter-spacing: 3px;
    margin-bottom: 0;
  }
}

.flip-hint {
  font-size: 12px;
  text-align: center;
  opacity: 0.8;
  margin-bottom: 0;
}

@media (prefers-reduced-motion: reduce) {
  .gift-card-inner {
    transition-duration: 0.01s;
  }
}

.gift-card {
  position: relative;
  overflow: hidden;
  padding: 20px;
  border-radius: 16px;
  color: #fff;
  display: flex;
  flex-direction: column;
  gap: 18px;
  background: linear-gradient(135deg, var(--primary) 0%, #1c1c3a 100%);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.25);

  &::before,
  &::after {
    content: "";
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 220px;
    height: 220px;
    top: -90px;
    right: -70px;
  }

  &::after {
    width: 160px;
    height: 160px;
    bottom: -80px;
    left: -50px;
  }

  > * {
    position: relative;
    z-index: 1;
  }
}

.gift-card-header {
  display: flex;
  align-items: center;
  gap: 14px;
}

.gift-card-image {
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  border-radius: 10px;
  background: #fff;
  padding: 4px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.gift-card-title {
  min-width: 0;

  .gift-card-label {
    font-size: 11px;
    letter-spacing: 2px;
    text-transform: uppercase;
    opacity: 0.75;
  }

  h2 {
    font-size: 16px;
    font-weight: bold;
    color: #fff;
    margin: 2px 0 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.gift-card-price {
  font-size: 28px;
  font-weight: bold;
  text-align: right;
}

.gift-card-data {
  display: flex;
  flex-direction: column;
  gap: 8px;

  .data-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.25);
  }

  .data-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
  }

  .copy-btn {
    flex-shrink: 0;
    width: auto;
    height: auto;
    padding: 6px 10px;
    font-size: 12px;
    font-weight: bold;
    color: #fff;
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.4);
    border-radius: 6px;
    cursor: pointer;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }

  .data-label {
    font-size: 11px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    opacity: 0.75;
    margin-bottom: 2vh;
  }

  .data-value {
    font-family: "Courier New", monospace;
    font-size: 15px;
    font-weight: bold;
    letter-spacing: 1px;
    word-break: break-all;
    user-select: all;
    margin-bottom: 1vh;
  }
}
</style>
